import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

function PanoramaViewer({ src, onClose }: { src: string; onClose: () => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<any>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const absUrl = src.startsWith('/') ? `${window.location.origin}${src}` : src;

    const buildScene = () => {
      if (!containerRef.current) return;
      containerRef.current.innerHTML = '';

      const scene = document.createElement('a-scene');
      scene.setAttribute('embedded', '');
      scene.setAttribute('vr-mode-ui', 'enabled: false');
      scene.setAttribute('loading-screen', 'enabled: false');
      scene.setAttribute('renderer', 'antialias: true');
      scene.style.position = 'absolute';
      scene.style.top = '0';
      scene.style.left = '0';
      scene.style.width = '100%';
      scene.style.height = '100%';

      const assets = document.createElement('a-assets');
      const img = document.createElement('img');
      img.id = 'pano-img';
      img.crossOrigin = 'anonymous';
      img.src = absUrl;
      assets.appendChild(img);
      scene.appendChild(assets);

      scene.addEventListener('loaded', () => setReady(true));
      setTimeout(() => setReady(true), 5000);

      const sky = document.createElement('a-sky');
      sky.setAttribute('src', '#pano-img');
      sky.setAttribute('rotation', '0 -90 0');
      scene.appendChild(sky);

      const rig = document.createElement('a-entity');
      rig.setAttribute('position', '0 1.6 0');
      const camera = document.createElement('a-camera');
      camera.setAttribute('look-controls', 'reverseMouseDrag: true; touchEnabled: true; magicWindowTrackingEnabled: false');
      camera.setAttribute('wasd-controls', 'enabled: false');
      camera.setAttribute('fov', '75');
      rig.appendChild(camera);
      scene.appendChild(rig);

      containerRef.current.appendChild(scene);
      sceneRef.current = scene;
    };

    if ((window as any).AFRAME) {
      buildScene();
    } else {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/aframe@1.6.0/dist/aframe-master.min.js';
      script.onload = () => setTimeout(buildScene, 100);
      document.head.appendChild(script);
    }

    return () => {
      if (sceneRef.current) {
        try { sceneRef.current.parentNode?.removeChild(sceneRef.current); } catch {}
        sceneRef.current = null;
      }
    };
  }, [src]);

  return (
    <div className="fixed inset-0 bg-black" style={{ zIndex: 9999 }}>
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 pointer-events-none" style={{ zIndex: 10000 }}>
        <span className="text-white text-sm font-bold">360° VR看房</span>
        <button onClick={onClose} className="pointer-events-auto w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white text-lg">&times;</button>
      </div>
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 10000 }}>
          <span className="text-white text-sm">加载全景中...</span>
        </div>
      )}
      <div className="absolute bottom-6 left-0 right-0 text-center pointer-events-none" style={{ zIndex: 10000 }}>
        <span className="bg-black/50 text-white/80 text-xs px-4 py-2 rounded-full">滑动屏幕 · 360°全景浏览</span>
      </div>
      <div ref={containerRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9999 }} />
    </div>
  );
}

interface RoomType {
  id: number;
  name: string;
  size: number;
  subsidy_amount: number;
  invite_threshold: number;
  current: number;
  image_url?: string;
}

interface CertRoom {
  room_type_id: number;
  eligible: boolean;
  has_cert: boolean;
  cert_id?: number;
}

const roomImages = [
  '/images/room_yi.png',
  '/images/room_er.png',
  '/images/room_san.png',
  '/images/room_si.png',
  '/images/room_wu.png',
];

const labelImages = [
  '/images/shenji_jingpin.png',
  '/images/shenji_remne.png',
  '/images/shenji_pinzhi.png',
  '/images/shenji_shushi.png',
  '/images/shenji_zuxniang.png',
];

function formatSubsidy(amount: number) {
  if (amount >= 10000) return `${amount / 10000}万基础生活现金补贴`;
  return `${amount}元基础生活现金补贴`;
}

export default function Shenji() {
  const navigate = useNavigate();
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [approvedInvites, setApprovedInvites] = useState(0);
  const [vrRoom, setVrRoom] = useState<string | null>(null);
  const [certRooms, setCertRooms] = useState<CertRoom[]>([]);
  const [certImage, setCertImage] = useState<string | null>(null);
  const [certLoading, setCertLoading] = useState<number | null>(null);

  useEffect(() => {
    api.get('/team/room-types').then(({ data }) => {
      const rooms = Array.isArray(data?.rooms) ? data.rooms : (Array.isArray(data) ? data : []);
      setRoomTypes(rooms);
      if (data?.current_invites !== undefined) setApprovedInvites(data.current_invites);
    }).catch(() => {});

    api.get('/property-certificate/check').then(({ data }) => {
      if (Array.isArray(data?.rooms)) setCertRooms(data.rooms);
    }).catch(() => {});
  }, []);

  const handleViewCert = async (roomTypeId: number) => {
    setCertLoading(roomTypeId);
    try {
      const { data } = await api.post('/property-certificate/generate', { room_type_id: roomTypeId });
      if (data?.image) {
        setCertImage(data.image);
      }
    } catch {
      // silently fail
    } finally {
      setCertLoading(null);
    }
  };

  const highestEligibleId = [...roomTypes].reverse().find(r => {
    const ci = certRooms.find(c => c.room_type_id === r.id);
    return ci?.eligible ?? (approvedInvites >= (r.invite_threshold || 1));
  })?.id;

  return (
    <div className="max-w-md mx-auto bg-[url(/images/shenji_beijing.png)] bg-[length:100%_100%] bg-no-repeat bg-top pb-[120px] min-h-screen relative">
      <AuthGuard />

      {/* Back button */}
      <div className="pt-4 px-4">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* Room type cards */}
      <div className="px-4 space-y-1  mt-[180px]">
        {roomTypes.map((rt, idx) => {
          const roomImg = roomImages[idx] || roomImages[roomImages.length - 1];
          const labelImg = labelImages[idx] || labelImages[labelImages.length - 1];
          const threshold = rt.invite_threshold || 1;
          const progress = Math.min(approvedInvites, threshold);
          const pct = (progress / threshold) * 100;
          const certInfo = certRooms.find(c => c.room_type_id === rt.id);
          const eligible = certInfo?.eligible ?? (approvedInvites >= threshold);
          const isHighestCert = eligible && highestEligibleId === rt.id;
          const isLoadingCert = certLoading === rt.id;

          return (
            <div key={rt.id} className="bg-gradient-to-b from-[#fff8ee] to-white rounded-xl overflow-hidden shadow">
              <div className="flex">
                {/* Left: room image with VR button */}
                <div className="w-[260px] relative flex-shrink-0 p-[5px]" onClick={() => setVrRoom(roomImg)}>
                  <img src={labelImg}  alt={rt.name} className="w-full h-full object-contain" />
                </div>

                {/* Right: info */}
                <div className="flex-1 p-2 flex flex-col justify-between">
                  <div>
                    <div className="flex items-baseline gap-1 -mb-[5px]">
                      <span className="text-[#333] text-md font-bold">{rt.size}m²</span>
                      <span className="text-[#333] text-xs font-bold">精装保障房</span>
                    </div>
                    <span className="text-[#666] text-[11px]">{formatSubsidy(rt.subsidy_amount)}</span>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-0">
                    <div className="flex items-center gap-1">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#c4161c] to-[#e53935] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[#999] text-[11px] flex-shrink-0">{progress}/{threshold}</span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="space-y-1">
                    <button
                      onClick={() => navigate('/invite')}
                      className="w-full bg-gradient-to-r from-[#c4161c] to-[#e53935] text-white text-[11px] font-bold py-0.5 rounded-lg"
                    >
                      立即邀请
                    </button>
                    <button
                      onClick={() => isHighestCert && handleViewCert(rt.id)}
                      disabled={!isHighestCert || isLoadingCert}
                      className={`w-full text-[10px] py-0.5 bg-[url(/images/shenji_fangchanzheng.png)] bg-no-repeat bg-[length:100%_100%] rounded-lg ${isHighestCert ? 'text-[#666]' : 'text-[#bbb] opacity-50'}`}
                    >
                      {isLoadingCert ? '生成中...' : '查看房产证'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className="w-full bg-[#fff] text-[#e53935] text-[11px] font-bold py-1.5 rounded-lg mt-2 text-center border border-[#fff]/30 cursor-pointer" onClick={() => navigate('/my-property')}>
          我的房产
        </div>
      </div>

      {/* VR Panorama Modal */}
      {vrRoom && <PanoramaViewer src={vrRoom} onClose={() => setVrRoom(null)} />}

      {/* Cert Image Modal */}
      {certImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => setCertImage(null)}>
          <div className="relative max-w-md w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setCertImage(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center text-gray-600 shadow-lg text-lg z-10"
            >
              &times;
            </button>
            <img src={certImage} alt="不动产权证书" className="w-full rounded-lg shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
}
